import type { PointData } from 'pixi.js';
import { Container, Graphics, Point, Sprite } from 'pixi.js';
import type { ResourceMap } from '../app/resources/types';
import { InventoryItem } from '../app/resources/types';
import { ItemRewardBubbleLayout } from '../../layouts/common/ItemRewardBubbleLayout';
import { setIconForInventory } from '../mainMenu/shopPage/utils/setIconForInventory';
import { repeat } from '../utils/misc-utils';
import { shuffle } from '../utils/array-utils';
import MathUtils from '../utils/MathUtils';
import RandomDataGenerator from '../libs/RandomDataGenerator';
import { interpolationMap } from '../libs/TweenManager/interpolations';
import { InventoryItemAmountView } from './InventoryItemAmountView';

interface AnimationParams {
  maxAngle: number;
  preferredAngle: number;
  preferredSpawnDelay: number;
  maxSpawnDelay: number;
  idleDistance: number;
  idleDistanceVariance: number;
  idleAngleVariance: number;
  idleWait: number;
  idlePivotRange: { min: number; max: number };
}

interface CollectRewardAnimationConfig {
  startPoint: Point | Container;
  resourceMap: ResourceMap;
  withLabel: boolean;
  coinHeartTarget?: Container;
  boosterTarget?: Container;
  animationParams?: Partial<AnimationParams>;
}

interface BubbleData {
  item: InventoryItem;
  idleOffset: PointData;
  spawnDelay: number;
  totalItemAmount: number;
}

const defaultParams: AnimationParams = {
  idleDistance: 200,
  idleWait: 200,
  maxAngle: 120,
  preferredAngle: 40,
  maxSpawnDelay: 200,
  preferredSpawnDelay: 40,
  idleDistanceVariance: 0.1,
  idleAngleVariance: 0.08,
  idlePivotRange: { min: 7, max: 15 },
};

const defaultParamsForLabel: AnimationParams = {
  idleDistance: 200,
  idleWait: 200,
  maxAngle: 120,
  preferredAngle: 40,
  maxSpawnDelay: 200,
  preferredSpawnDelay: 0,
  idleDistanceVariance: 0,
  idleAngleVariance: 0,
  idlePivotRange: { min: 0, max: 0 },
};

const rnd = new RandomDataGenerator([Date.now()]);

export class CollectRewardAnimator extends Container {
  private readonly animationParams: AnimationParams;

  constructor(private readonly config: CollectRewardAnimationConfig) {
    super();

    this.animationParams = {
      ...(config.withLabel ? defaultParamsForLabel : defaultParams),
      ...(config.animationParams ?? {}),
    };

    this.init();
  }

  private init(): void {
    this.generateBubbleData().forEach(async (bubbleData) => {
      await this.wait(bubbleData.spawnDelay);
      if (this.destroyed) return;
      const bubbleView = this.spawnBubble(bubbleData);
      await this.animateBubble(bubbleView, bubbleData);
    });
  }

  private spawnBubble(bubbleData: BubbleData): Container {
    const bubbleView = this.createBubble(bubbleData.item, bubbleData.totalItemAmount);
    bubbleView.props({
      parent: this.pixi.stage,
      position: this.getStartPoint(),
      scale: 0,
    });
    return bubbleView;
  }

  private async animateBubble(view: Container, bubbleData: BubbleData): Promise<void> {
    const { idleWait } = this.animationParams;

    await this.animateIdle(view, bubbleData);
    await this.wait(idleWait);
    await this.animateToTarget(view, bubbleData);
  }

  private animateIdle(view: Container, bubbleData: BubbleData): Promise<void> {
    const { idleOffset } = bubbleData;
    const { idlePivotRange } = this.animationParams;
    // TODO add params;

    const pivotTweenDuration = rnd.integerInRange(3000, 5000);

    const pivotTween = view.tweenTo(
      { pivotY: rnd.realInRange(idlePivotRange.min, idlePivotRange.max) * rnd.sign() },
      pivotTweenDuration,
      'sinInOut',
      true,
      0,
      -1,
      true,
    );
    pivotTween.fastForward(rnd.real() * pivotTweenDuration);

    view.tweenTo({ x: view.x + idleOffset.x, y: view.y + idleOffset.y }, 400, 'cubicOut', true);
    return view.tweenTo({ scaleXY: 1 }, 400, 'cubicOut', true).asyncOnComplete();
  }

  private animateToTarget(view: Container, bubbleData: BubbleData): Promise<void> {
    const target = this.getTargetPoint(bubbleData.item);

    const tweenProps = {
      x: [view.x > target.x ? view.x - 300 : view.x + 300, target.x],
      y: [view.y, target.y],
    };

    return view
      .tweenTo(tweenProps, 900, 'cubicIn', false)
      .interpolation(interpolationMap.bezier)
      .start()
      .asyncOnComplete();
  }

  private generateBubbleData(): BubbleData[] {
    const { resourceMap } = this.config;

    const angles = shuffle(this.getIdleAngles());

    const bubbleData: BubbleData[] = [];

    Object.entries(resourceMap).forEach(([key, amount], index) => {
      const itemBubbles: BubbleData[] = [];

      const item = key as InventoryItem;

      const partAmount = this.getBubblesAmount(item, amount);
      const delay = this.getSpawnDelayValue(partAmount);

      repeat(partAmount, (i) => {
        itemBubbles.push({
          item,
          idleOffset: this.getIdleOffset(angles[index]),
          spawnDelay: delay * i,
          totalItemAmount: amount,
        });
      });

      bubbleData.push(...itemBubbles);
    });

    return bubbleData;
  }

  private getBubblesAmount(item: InventoryItem, amount: number): number {
    const { withLabel, resourceMap } = this.config;

    if (withLabel) return 1;

    let partAmount = amount;

    if (item === InventoryItem.Coin) {
      const justCoins = Object.keys(resourceMap).length === 1;
      // in case of resource map has only coins generate more particles
      partAmount = Math.min(20, Math.floor(amount / (justCoins ? 2 : 5)));
    } else if (item === InventoryItem.UnlimitedLivesMinutes) {
      partAmount = 1;
    }

    return partAmount;
  }

  private getIdleOffset(angle: number): PointData {
    const { idleDistance, idleDistanceVariance, idleAngleVariance } = this.animationParams;
    const finalDistance = idleDistance * (1 + rnd.realInRange(-idleDistanceVariance, idleDistanceVariance));
    const finalAngle = angle * (1 + rnd.realInRange(-idleAngleVariance, idleAngleVariance));
    return MathUtils.getPointOnCircle(0, 0, finalDistance, finalAngle);
  }

  private getSpawnDelayValue(itemAmount: number): number {
    const { maxSpawnDelay, preferredSpawnDelay } = this.animationParams;
    return Math.min(preferredSpawnDelay, maxSpawnDelay * (itemAmount - 1));
  }

  private getIdleAngles(): number[] {
    const { resourceMap } = this.config;
    const { maxAngle, preferredAngle } = this.animationParams;

    const typesAmount = Object.keys(resourceMap).length;
    const angleDiff = Math.min(preferredAngle, maxAngle / (typesAmount - 1));
    const startAngle = angleDiff * (typesAmount - 1) * -0.5;
    const rotationToUp = -90;

    return new Array(typesAmount)
      .fill(0)
      .map((_, i) => MathUtils.degreesToRads(rotationToUp + startAngle + i * angleDiff));
  }

  private createBubble(item: InventoryItem, amount: number): Container {
    const { withLabel } = this.config;

    if (withLabel) {
      return new InventoryItemAmountView(item, amount, true);
    }

    const layout = new ItemRewardBubbleLayout();
    setIconForInventory(layout.icon, item);
    return layout.root;
  }

  private getStartPoint(): PointData {
    const { startPoint } = this.config;
    return startPoint instanceof Point ? startPoint : startPoint.getGlobalPosition();
  }

  private getTargetPoint(item: InventoryItem): PointData {
    const coinOrHeart = this.isCoinOrLive(item);
    const displayTarget = coinOrHeart ? this.config.coinHeartTarget : this.config.boosterTarget;

    if (displayTarget) {
      return displayTarget.getGlobalPosition();
    }

    return coinOrHeart
      ? { x: -100, y: 100 }
      : { x: this.pixi.renderer.width * 0.5, y: this.pixi.renderer.height + 100 };
  }

  private isCoinOrLive(item: InventoryItem): boolean {
    return item === InventoryItem.Coin || item === InventoryItem.Lives || item === InventoryItem.UnlimitedLivesMinutes;
  }
}

export function testRewardCollector(): void {
  const background = new Graphics();
  background.rect(0, 0, 3000, 3000);
  background.fill({ color: 0, alpha: 0.7 });
  background.props({ parent: background.pixi.stage });

  [
    {
      [InventoryItem.Coin]: 5,
    },
    {
      [InventoryItem.Coin]: 20,
    },
    {
      [InventoryItem.Coin]: 50,
    },
    {
      [InventoryItem.Coin]: 100,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.Coin]: 10,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.BoosterRedraw]: 3,
      [InventoryItem.Coin]: 10,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.BoosterRedraw]: 3,
      [InventoryItem.BoosterUndo]: 1,
      [InventoryItem.Coin]: 10,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.BoosterRedraw]: 3,
      [InventoryItem.BoosterUndo]: 1,
      [InventoryItem.BoosterHammer]: 3,
      [InventoryItem.Coin]: 10,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.BoosterRedraw]: 3,
      [InventoryItem.BoosterUndo]: 1,
      [InventoryItem.BoosterHammer]: 3,
      [InventoryItem.Lives]: 3,
      [InventoryItem.Coin]: 10,
    },
    {
      [InventoryItem.BoosterMove]: 3,
      [InventoryItem.BoosterRedraw]: 3,
      [InventoryItem.BoosterUndo]: 1,
      [InventoryItem.BoosterHammer]: 3,
      [InventoryItem.Lives]: 3,
      [InventoryItem.UnlimitedLivesMinutes]: 40,
      [InventoryItem.Coin]: 10,
    },
  ].forEach((resourceMap, index) => {
    const x = 200 + (index % 5) * 300;
    const y = 500 + Math.floor(index / 5) * 400;

    const chest = new Sprite().props({
      parent: background.pixi.stage,
      x,
      y,
      texture: 'Chest_Gold',
      anchor: 0.5,
      eventMode: 'static',
    });

    chest.onpointertap = (): void => {
      void new CollectRewardAnimator({
        resourceMap,
        startPoint: chest,
        withLabel: false,
      });
    };
  });
}
